import api from './api';

/**
 * Servicio de cotizaciones cd-2b para el consultor (Partner Portal).
 * Consume las mismas rutas que el AdminPanel (/v1/cd2b/propuestas/*); el token
 * del portal (rol consultant) las autoriza. SIN mock data (app legal).
 */
const cd2bPropuestaService = {
  // Catálogo para armar el paquete: ediciones, módulos curados, seats por edición.
  async getCatalogo() {
    const response = await api.get('/cd2b/propuestas/catalogo');
    return response.data;
  },
  /**
   * Catálogos FISCALES del MH (actividad económica CAT-019 + geografía SV) para capturar la
   * identidad fiscal de la empresa real.
   *
   * Endpoint público y ya existente —el mismo que usa el alta del portal— en vez de colgarlos
   * de `/catalogo`: son ~1.100 entradas inmutables con cache de 24 h, y `/catalogo` se pide en
   * cada apertura del editor.
   */
  /**
   * Países habilitados en cd-2b (endpoint público existente; `estado:'roadmap'` = aún no se
   * vende ahí). Anti-fallback: no hay lista estática de respaldo — si falla, el selector de país
   * lo dice y no se puede crear la empresa, en vez de ofrecer un país que quizá no esté habilitado.
   */
  async getPaises() {
    const response = await api.get('/public/cd2b/paises');
    return response.data;
  },
  async getCatalogosFiscales() {
    const response = await api.get('/public/cd2b/catalogos', { params: { country: 'SV' } });
    return response.data;
  },
  /**
   * Las ENTRADAS DE MENÚ que verá el cliente con ese paquete. La vista previa enseñaba ÁREAS
   * («Inventario»), pero el cliente cuenta PANTALLAS: nadie sabía si un `micro` abriría el ERP
   * con 9 o con 26 entradas hasta que el dueño lo abría. Cálculo puro en el servidor, con el
   * mismo AND (área × rubro × régimen fiscal) que aplica el escritorio.
   */
  async previsualizarMenu({ edicion, giro, modulos = [], contribuyente = true }) {
    const response = await api.get('/cd2b/menu-preview', {
      params: {
        edicion,
        ...(giro ? { giro } : {}),
        ...(modulos.length ? { modulos: modulos.join(',') } : {}),
        ...(contribuyente ? {} : { contribuyente: '0' }),
      },
    });
    return response.data;
  },
  // Precio AUTORITATIVO del paquete (+ comisión del asesor). El editor pinta el total en vivo con
  // su espejo; esto confirma el número del backend y trae la comisión (que depende del partner).
  async cotizar(payload) {
    const response = await api.post('/cd2b/propuestas/cotizar', payload);
    return response.data;
  },
  async getAll(params = {}) {
    const response = await api.get('/cd2b/propuestas', { params });
    return response.data;
  },
  async getById(id) {
    const response = await api.get(`/cd2b/propuestas/${id}`);
    return response.data;
  },
  async create(data) {
    const response = await api.post('/cd2b/propuestas', data);
    return response.data;
  },
  async update(id, data) {
    const response = await api.put(`/cd2b/propuestas/${id}`, data);
    return response.data;
  },
  // Borra lo que nunca salió al cliente (borrador/rechazada/cancelada). El backend responde 409
  // si ya se envió o si tiene empresa demo: ahí lo que toca es cancelar.
  async eliminar(id) {
    const response = await api.delete(`/cd2b/propuestas/${id}`);
    return response.data;
  },
  // Cancela una cotización ya enviada: deja de estar viva sin borrar que existió.
  async cancelar(id, motivo) {
    const response = await api.post(`/cd2b/propuestas/${id}/cancelar`, { motivo });
    return response.data;
  },
  async enviar(id) {
    const response = await api.post(`/cd2b/propuestas/${id}/enviar`);
    return response.data;
  },
  // `evidencia` es obligatoria para el asesor (como aceptó el cliente): la exige el api.
  async aprobar(id, evidencia = '') {
    const response = await api.post(`/cd2b/propuestas/${id}/aprobar`, { evidencia });
    return response.data;
  },
  // Único envío de verdad: manda el correo al dueño con el enlace y pasa a 'enviada'.
  // `incluirAccesoDemo` solo ANUNCIA la empresa de prueba; las credenciales se revelan en la
  // página con token, nunca en el cuerpo del correo.
  async enviarAlDueno(id, { incluirAccesoDemo = true } = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/enviar-dueno`, { incluirAccesoDemo })
    return response.data
  },

  async generarLink(id) {
    const response = await api.post(`/cd2b/propuestas/${id}/link`);
    return response.data;
  },
  /** ¿Ya pagó? Consulta activa a la pasarela; devuelve también el enlace vigente, si lo hay. */
  async estadoPago(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/estado-pago`);
    return response.data;
  },

  // Pago conciliado fuera de Stripe. Registra el cobro; NO pasa la empresa a producción.
  async registrarPagoManual(id, data = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/activar-manual`, data);
    return response.data;
  },
  // Lista de verificación previa: { listo, bloqueos[], advertencias[] }.
  async listoParaProduccion(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/listo-para-produccion`);
    return response.data;
  },
  // La demo deja de serlo. 409 DATOS_PRUEBA_SIN_DECIDIR si nadie decidió qué hacer con ellos.
  async pasarAProduccion(id, data = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/pasar-a-produccion`, data);
    return response.data;
  },
  async extenderDemo(id, dias) {
    const response = await api.post(`/cd2b/propuestas/${id}/extender-demo`, { dias });
    return response.data;
  },
  // Crea la EMPRESA DEMO editable (tenant nube real, esDemo) para que el dueño la pruebe.
  // Devuelve { negocioCodigo, pinAdmin, tenantId, host } — comparte el código con el dueño.
  async crearDemo(id) {
    const response = await api.post(`/cd2b/propuestas/${id}/crear-demo`);
    return response.data;
  },
  // Reobtiene el acceso del dueño a la empresa demo (código de empresa + PIN admin).
  async accesoDemo(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/acceso-demo`);
    return response.data;
  },
  // Credenciales de los puestos de la empresa demo (usuario + PIN) para entregárselas al
  // prospecto. Se recalculan en el servidor a partir del seeder: no hay secretos guardados.
  async credencialesDemo(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/credenciales-demo`);
    return response.data;
  },
  // ── Datos de prueba y migración (docs/71 P5-P6, P10-P11) ──────────────────
  // Las cargas comparten puerta: lo sembrado como práctica y lo importado del sistema
  // anterior se listan y se revierten por las MISMAS rutas. Una sola forma de borrar.
  async listarDatosPrueba(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/datos-prueba`);
    return response.data;
  },
  // Chequeo previo: qué pide la plantilla del giro contra lo cotizado, con el precio de cada
  // brecha. No escribe nada y no exige empresa demo (docs/78 F5).
  async chequeoSiembra(id, giro) {
    const response = await api.get(`/cd2b/propuestas/${id}/datos-prueba/chequeo`, {
      params: giro ? { giro } : {},
    });
    return response.data;
  },
  // Deja la empresa de práctica SIN operación para volver a sembrarla limpia. Alcanza lo que
  // la reversión por carga no puede tocar (lo tecleado a mano, lo del sistema anterior).
  // `previsualizar: true` cuenta sin borrar: esto es destructivo y no se confirma a ciegas.
  async vaciarDatosDemo(id, { previsualizar = false } = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/datos-prueba/vaciar`, { previsualizar });
    return response.data;
  },
  async sembrarDatosPrueba(id, data = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/datos-prueba`, data);
    return response.data;
  },
  // La siembra de operación (12 meses) responde 202 y sigue en segundo plano: no cabe en
  // una petición HTTP (docs/78 F7). Esto pregunta por su avance hasta 'listo' o 'error'.
  async estadoSiembra(id, cargaId) {
    const response = await api.get(`/cd2b/propuestas/${id}/datos-prueba/estado/${cargaId}`);
    return response.data;
  },
  // `decision`: 'conservar' | 'quitarEjemplo' | 'vaciar' — qué pasa con los datos al entregar.
  async decidirDatosPrueba(id, decision) {
    const response = await api.post(`/cd2b/propuestas/${id}/datos-prueba/decision`, { decision });
    return response.data;
  },
  // `previsualizar: true` cuenta sin borrar. Nunca "todo o nada" a ciegas.
  async revertirDatosPrueba(id, cargaId, { previsualizar = false } = {}) {
    const response = await api.delete(
      `/cd2b/propuestas/${id}/datos-prueba/${cargaId}${previsualizar ? '?previsualizar=1' : ''}`,
    );
    return response.data;
  },
  // ── PROYECTO de migración (F2/F3): la mesa de trabajo ──────────────────────
  // El asistente de un archivo suelto sigue existiendo (`subirArchivoMigracion`); esto es
  // el proceso largo, con su bandeja, su plan y su carga reanudable.
  async crearProyectoMigracion(id, data) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto`, data || {});
    return response.data;
  },
  async listarProyectosMigracion(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/proyecto`);
    return response.data;
  },
  async estadoProyectoMigracion(id, pid) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}`);
    return response.data;
  },
  /**
   * Sube N archivos de una vez en `multipart/form-data`. En base64 dentro de un JSON, un
   * libro de 20 MB son 27 MB de cadena y el navegador no puede informar del avance.
   */
  async subirArchivosProyecto(id, pid, archivos, onProgreso) {
    const fd = new FormData();
    for (const f of archivos) fd.append('archivos', f, f.name);
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/archivos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgreso,
    });
    return response.data;
  },
  async clasificarHojasProyecto(id, pid) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/clasificar`);
    return response.data;
  },
  async detalleHojaProyecto(id, pid, hid) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/hoja/${hid}`);
    return response.data;
  },
  async ajustarHojaProyecto(id, pid, hid, cambios) {
    const response = await api.patch(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/hoja/${hid}`, cambios);
    return response.data;
  },
  async ensayarHojaProyecto(id, pid, hid) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/hoja/${hid}/ensayo`);
    return response.data;
  },
  async cargarProyecto(id, pid, data) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/cargar`, data || {});
    return response.data;
  },
  /** Deshace la carga de UN tipo de dato. `previsualizar` cuenta sin borrar. */
  async deshacerCargaProyecto(id, pid, entidad, { previsualizar = false } = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/deshacer`, {
      entidad,
      previsualizar,
    });
    return response.data;
  },
  // Vacía la empresa de prueba desde la migración. Con `recargar`, vuelve a cargar todo lo del
  // proyecto justo después (202: el avance sigue por `progreso`). `previsualizar` cuenta sin borrar.
  async vaciarEmpresaProyecto(id, pid, { previsualizar = false, recargar = false } = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/vaciar`, {
      previsualizar,
      recargar,
    });
    return response.data;
  },
  async progresoProyecto(id, pid) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/progreso`);
    return response.data;
  },
  /** Qué necesita este cliente que cd-2b no tiene. Detecta; no decide nada. */
  async detectarHallazgos(id, pid, contexto) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/hallazgos`, contexto || {});
    return response.data;
  },
  async decidirHallazgo(id, pid, hallazgoId, decision, nota) {
    const response = await api.post(
      `/cd2b/propuestas/${id}/migracion/proyecto/${pid}/hallazgos/${encodeURIComponent(hallazgoId)}`,
      { decision, nota }
    );
    return response.data;
  },
  /** La IA propone para una hoja. NO aplica nada: deja una propuesta que hay que decidir. */
  async sugerirIAHoja(id, pid, hid) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/hoja/${hid}/sugerencia`);
    return response.data;
  },
  async decidirSugerenciaHoja(id, pid, hid, aceptar) {
    const response = await api.post(
      `/cd2b/propuestas/${id}/migracion/proyecto/${pid}/hoja/${hid}/sugerencia/decision`,
      { aceptar }
    );
    return response.data;
  },
  async cuadrarProyecto(id, pid, declarado) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/cuadre`, { declarado });
    return response.data;
  },
  /** Mueve el `estado` real del proyecto — para que el paso visible sobreviva a un recargo. */
  async cambiarEstadoProyecto(id, pid, estado) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/estado`, { estado });
    return response.data;
  },
  /** "Guardar y continuar después": registra la pausa, devuelve la checklist para confirmar. */
  async pausarProyecto(id, pid) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/pausar`);
    return response.data;
  },
  // ── Migración conversacional (chat de catálogo) ─────────────────────────────
  /** `entidad` opcional — 'productos' por default en el backend si se omite. */
  async chatAbrirProyecto(id, pid, entidad) {
    const response = await api.post(
      `/cd2b/propuestas/${id}/migracion/proyecto/${pid}/chat/abrir`,
      entidad ? { entidad } : undefined
    );
    return response.data;
  },
  async chatMensajeProyecto(id, pid, mensaje, hojaId) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/chat/mensaje`, { mensaje, hojaId });
    return response.data;
  },
  async chatFotoProyecto(id, pid, file, hojaId) {
    const fd = new FormData();
    fd.append('foto', file, file.name);
    if (hojaId) fd.append('hojaId', hojaId);
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/chat/foto`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async chatConfirmarProyecto(id, pid, hojaId) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/chat/confirmar`, { hojaId });
    return response.data;
  },
  async chatEstadoProyecto(id, pid, hojaId) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/chat`, {
      params: hojaId ? { hojaId } : undefined,
    });
    return response.data;
  },
  /** Copiloto persistente: acompaña los 4 pasos, no solo Recopilar como el chat de arriba. */
  async copilotoMensajeProyecto(id, pid, mensaje, paso, hojaActiva, opcionElegida) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/copiloto/mensaje`, {
      mensaje,
      paso,
      hojaActiva,
      opcionElegida: Number.isInteger(opcionElegida) ? opcionElegida : undefined,
    });
    return response.data;
  },
  async copilotoEstadoProyecto(id, pid) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/copiloto`);
    return response.data;
  },
  async copilotoLimpiarProyecto(id, pid) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/copiloto/limpiar`);
    return response.data;
  },
  /** El link para que el dueño termine el wizard sin cuenta. Genera uno nuevo invalida el anterior. */
  async generarEnlacePublicoProyecto(id, pid) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/enlace-publico`);
    return response.data;
  },
  /** La plantilla .xlsx de un tipo de dato, generada de la especificación del motor. */
  async plantillaMigracion(id, entidad) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/plantilla/${encodeURIComponent(entidad)}`, {
      responseType: 'blob',
    });
    return response.data;
  },
  /** El archivo del cliente, devuelto con las celdas problemáticas marcadas. */
  async correccionesProyecto(id, pid, hojaId) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/correcciones`, {
      params: hojaId ? { hojaId } : undefined,
      responseType: 'blob',
    });
    return response.data;
  },
  async paqueteProyecto(id, pid) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}/paquete`);
    return response.data;
  },
  async descartarProyectoMigracion(id, pid) {
    const response = await api.delete(`/cd2b/propuestas/${id}/migracion/proyecto/${pid}`);
    return response.data;
  },
  async especificacionMigracion(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/especificacion`);
    return response.data;
  },
  // Sube el Excel/CSV (o el texto pegado). Devuelve encabezados, muestra y mapeo sugerido.
  // ── EL CATÁLOGO DEL CONTADOR, ANTES DE CREAR LA EMPRESA ────────────────────
  //
  // Las tres corren sin inquilino: leer el archivo, derivar qué significa la numeración del
  // contador y decidir qué cuenta suya es cada concepto es lógica pura. Por eso «Crear
  // empresa» puede esperar a que esté hecho en vez de crear a ciegas.

  /** Sube el catálogo (o texto pegado) y devuelve los grupos que hay que confirmar. */
  async analizarCatalogoContador(id, { archivo, texto }) {
    const fd = new FormData();
    if (archivo) fd.append('archivo', archivo);
    if (texto) fd.append('texto', texto);
    const response = await api.post(`/cd2b/propuestas/${id}/catalogo-contador/analizar`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  /**
   * Con los grupos confirmados, qué cuenta del catálogo es cada concepto.
   *
   * Acepta confirmaciones PARCIALES y las guarda: nadie resuelve cien conceptos de una
   * sentada, y perder el trabajo a medias es la forma más segura de que no se termine.
   */
  async conciliarCatalogoContador(id, { decisiones, confirmaciones } = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/catalogo-contador/conciliar`, {
      ...(decisiones ? { decisiones } : {}),
      ...(confirmaciones ? { confirmaciones } : {}),
    });
    return response.data;
  },
  /**
   * El estado guardado, para volver donde se dejó.
   *
   * Incluye si el freno aplica (`propio`), QUIÉN lo dijo (`origen`: el levantamiento, esta
   * cotización, o nadie todavía) y los datos del contador que autorizó el catálogo.
   */
  async estadoCatalogoContador(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/catalogo-contador`);
    return response.data;
  },
  /**
   * Con la empresa ya creada, trae las cuentas del catálogo que le faltan. Por defecto es un
   * ensayo: dice cuántas faltan, cuántas ya están y cuáles quedan fuera, sin escribir nada.
   */
  /**
   * El enlace para que el contador del cliente conteste el catálogo sin cuenta. Si hay uno
   * vigente devuelve el mismo; `renovar` emite otro y el anterior deja de abrir.
   */
  async emitirEnlaceCatalogoContador(id, { renovar = false } = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/catalogo-contador/enlace`, { renovar });
    return response.data;
  },
  async completarCatalogoContador(id, { ensayo = true } = {}) {
    const response = await api.post(`/cd2b/propuestas/${id}/catalogo-contador/completar`, { ensayo });
    return response.data;
  },
  /**
   * Contesta desde la cotización «¿este cliente trae el catálogo de su contador?».
   *
   * Para las altas que no pasan por levantamiento, que era donde vivía la única pregunta. Si
   * el levantamiento SÍ la contestó, el api responde 409: manda él, y se cambia allí.
   */
  async declararCatalogoContador(id, { propio, contador } = {}) {
    const response = await api.put(`/cd2b/propuestas/${id}/catalogo-contador/declaracion`, {
      propio,
      ...(contador ? { contador } : {}),
    });
    return response.data;
  },

  async subirArchivoMigracion(id, data) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/archivo`, data);
    return response.data;
  },
  // `dryRun: true` valida sin escribir; es el mismo código que la carga real.
  async cargarMigracion(id, data) {
    const response = await api.post(`/cd2b/propuestas/${id}/migracion/cargar`, data);
    return response.data;
  },
  // Calidad de los datos y exportación (solo lectura).
  async auditoriaCalidad(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/auditoria`);
    return response.data;
  },
  async exportarDatosCliente(id) {
    const response = await api.get(`/cd2b/propuestas/${id}/migracion/exportar`);
    return response.data;
  },
  // Perfiles de mapeo del ASESOR: el trabajo de mapear un origen se hace una vez y sirve
  // para toda su cartera (muchas empresas salen del mismo sistema anterior).
  async listarPerfilesMapeo(entidad) {
    const response = await api.get('/cd2b/propuestas/perfiles-mapeo/lista', { params: entidad ? { entidad } : {} });
    return response.data;
  },
  async guardarPerfilMapeo(data) {
    const response = await api.post('/cd2b/propuestas/perfiles-mapeo/guardar', data);
    return response.data;
  },
  async marcarPerfilUsado(pid) {
    const response = await api.post(`/cd2b/propuestas/perfiles-mapeo/${pid}/usado`);
    return response.data;
  },
  async eliminarPerfilMapeo(pid) {
    const response = await api.delete(`/cd2b/propuestas/perfiles-mapeo/${pid}`);
    return response.data;
  },
  async descartarArchivoMigracion(id, archivoId) {
    const response = await api.delete(`/cd2b/propuestas/${id}/migracion/${archivoId}`);
    return response.data;
  },

  // Solicita un plugin/desarrollo a medida al equipo CoreDesk para esta propuesta.
  async solicitarPlugin(id, data) {
    const response = await api.post(`/cd2b/propuestas/${id}/plugin`, data);
    return response.data;
  },
  async listarPlugins(params = {}) {
    const response = await api.get('/cd2b/propuestas/plugins/lista', { params });
    return response.data;
  },
  async actualizarPlugin(pid, data) {
    const response = await api.patch(`/cd2b/propuestas/plugins/${pid}`, data);
    return response.data;
  },
};

export default cd2bPropuestaService;
